import I18n from 'I18n';
import UISelect from 'UIComponents/input/UISelect';
import virtualizedMenuRenderer from 'UIComponents/input/utils/virtualizedMenuRenderer';
import { Avatar } from 'avatar-components/Avatar';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { List as ImmutableList } from 'immutable';
import { useCallback, useEffect, useMemo } from 'react';
import { convertListToArray } from '../../shared/utils/immutable';
import { useTeamOptions } from '../../team/hooks/useTeamOptions';
import { useSelectUsersAndTeams } from '../hooks/useSelectUsersAndTeams';
import { useUsersAndTeamsPlaceholder } from '../hooks/useUsersAndTeamsPlaceholder';
import { searchResults } from '../operators/searchResults';
import { convertTeamsToOptions } from '../utils/convert-teams';
import AgentSelectItem from './AgentSelectItem';
import ChatFlowInboxAccessTooltip from './tooltips/ChatFlowInboxAccessTooltip';
import { jsx as _jsx } from "react/jsx-runtime";
const renderAvatar = email => {
  if (!email) {
    return null;
  }
  return /*#__PURE__*/_jsx("div", {
    style: {
      width: '16px',
      height: '16px'
    },
    children: /*#__PURE__*/_jsx(Avatar, {
      size: "xxs",
      hubSpotUserEmail: email
    })
  });
};
const getGroupedOptionsFromLists = (usersOptions, teamsOptions, inboxId, hideUserSectionTooltip) => {
  const groupOptions = [{
    text: I18n.text('routing-ui.users-and-teams.groups.team'),
    options: teamsOptions
  }, {
    text: I18n.text('routing-ui.users-and-teams.groups.user'),
    dropdownText: typeof inboxId !== 'undefined' && !hideUserSectionTooltip ? /*#__PURE__*/_jsx(ChatFlowInboxAccessTooltip, {
      inboxId: `${inboxId}`
    }) : null,
    options: usersOptions.map(user => Object.assign({}, user, {
      avatar: renderAvatar(user.email)
    }))
  }];
  return groupOptions;
};
const checkWithImmutable = props => props.withImmutable === true;
export const NewUsersAndTeamsSelect = props => {
  // Note: doing this so that we can allow Immutable and non-Immutable values to
  // be sent to `handleChange`; this is a temporary solution until we remove Immutable
  // support completely
  const {
    'data-test-id': testId,
    disabled,
    dropDownFooter,
    error,
    hideUserSectionTooltip,
    inboxId,
    isLoading,
    itemComponent,
    objectTypeId,
    onMount,
    routingSource,
    selectedTeamIds = [],
    selectedUserIds = [],
    usersOptions,
    valueComponent = AgentSelectItem
  } = props;
  const convertTeams = useCallback(teamsToConvert => convertTeamsToOptions(teamsToConvert, 1, routingSource), [routingSource]);
  const {
    data: teams,
    loading: loadingTeams,
    teams: teamOptions
  } = useTeamOptions({
    convertToOptions: convertTeams,
    objectTypeId,
    routingSource,
    skipFetch: false,
    workspaceId: Number(inboxId)
  });
  const optionIds = useMemo(() => {
    const idsForTeams = teamOptions.map(team => team.value);
    const idsForUsers = usersOptions.map(user => user.value);
    return {
      teams: idsForTeams,
      users: idsForUsers
    };
  }, [teamOptions, usersOptions]);
  const options = useMemo(() => getGroupedOptionsFromLists(usersOptions, teamOptions, inboxId, hideUserSectionTooltip), [hideUserSectionTooltip, inboxId, teamOptions, usersOptions]);
  const {
    teamIds,
    userIds
  } = useMemo(() => ({
    teamIds: convertListToArray(selectedTeamIds),
    userIds: convertListToArray(selectedUserIds)
  }), [selectedTeamIds, selectedUserIds]);
  const {
    onSelect,
    selectedIds
  } = useSelectUsersAndTeams({
    optionIds,
    teamIds,
    userIds,
    teams
  });
  const placeholder = useUsersAndTeamsPlaceholder(teamOptions, usersOptions);

  // TODO: can we get rid of this if we pull users in?
  useEffect(() => {
    if (onMount) {
      onMount();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (loadingTeams || isLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {});
  }
  const onChange = event => {
    const newIds = onSelect(event);

    // we use `props` here to satisfy TS which won't understand the type of `handleChange`
    // if we call it directly
    // TODO: remove when Immutable support is dropped
    if (checkWithImmutable(props)) {
      props.handleChange(ImmutableList(newIds.userIds), ImmutableList(newIds.teamIds));
    } else {
      props.handleChange(newIds.userIds, newIds.teamIds);
    }
  };
  return /*#__PURE__*/_jsx(FormControl, {
    error: !!error,
    validationMessage: error || null,
    children: /*#__PURE__*/_jsx(UISelect, {
      anchorType: "button",
      cache: false,
      className: "users-and-teams-select",
      "data-test-id": testId,
      disabled: disabled,
      dropdownFooter: dropDownFooter,
      filterOptions: searchResults,
      isLoading: isLoading,
      itemComponent: itemComponent,
      menuRenderer: virtualizedMenuRenderer,
      menuStyle: {
        maxHeight: 320
      },
      multi: true,
      onChange: onChange,
      options: options,
      placeholder: placeholder,
      value: selectedIds,
      valueComponent: valueComponent
    })
  });
};
NewUsersAndTeamsSelect.displayName = 'NewUsersAndTeamsSelect';
export default NewUsersAndTeamsSelect;